import { supabase } from './supabase';
import { toDashboardUiError } from './dashboard-errors';

export type CrmDeal = { id: string; name: string | null; stage: string | null; amount: string | null; closeDate: string | null };
export type CrmNote = { id: string; body: string; timestamp: string | null };
export type CrmContact = { id: string; name: string | null; email: string | null; jobTitle: string | null; phone: string | null };
export type CrmAccount =
  | { status: 'not_synced' }
  | {
    status: 'synced';
    observedAt: string | null;
    company: {
      domain: string | null; industry: string | null; city: string | null; country: string | null;
      /** Account header (26/10): stage, size and owner as HubSpot holds them. */
      website?: string | null; lifecycleStage?: string | null; employees?: string | null; employeeRange?: string | null;
      ownerName?: string | null; ownerEmail?: string | null;
    };
    associationsStatus: 'complete' | 'partial' | 'unavailable' | 'unknown';
    deals: CrmDeal[]; dealsTotal: number;
    contacts: CrmContact[]; contactsTotal: number;
    notesStatus: 'complete' | 'partial' | 'unavailable' | 'unknown';
    notes: CrmNote[]; notesTotal: number;
  };

const text = (value: unknown): string | null => typeof value === 'string' && value.trim() ? value.trim() : null;
const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
const records = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? value.flatMap((item) => { const row = record(item); return row ? [row] : []; }) : [];

/** Exact HubSpot dossier of a Domino account (R2). Null: not a CRM account. */
export const readCrmAccount = async (subjectId: string): Promise<CrmAccount | null> => {
  const { data, error } = await supabase.rpc('read_crm_account_r1', { p_subject_id: subjectId });
  if (error) throw new Error('Les informations HubSpot n’ont pas pu être chargées.');
  const dossier = record(data);
  if (!dossier) return null;
  if (dossier.status !== 'synced') return { status: 'not_synced' };
  const company = record(dossier.company) ?? {};
  const links = dossier.associationsStatus;
  return {
    status: 'synced',
    observedAt: text(dossier.observedAt),
    company: {
      domain: text(company.domain), industry: text(company.industry), city: text(company.city), country: text(company.country),
      website: text(company.website), lifecycleStage: text(company.lifecycleStage), employees: text(company.employees),
      employeeRange: text(company.employeeRange), ownerName: text(company.ownerName), ownerEmail: text(company.ownerEmail),
    },
    associationsStatus: links === 'complete' || links === 'partial' || links === 'unavailable' ? links : 'unknown',
    deals: records(dossier.deals).map((deal) => ({ id: String(deal.id ?? ''), name: text(deal.name), stage: text(deal.stage), amount: text(deal.amount), closeDate: text(deal.closeDate) })),
    dealsTotal: typeof dossier.dealsTotal === 'number' ? dossier.dealsTotal : 0,
    contacts: records(dossier.contacts).map((contact) => ({ id: String(contact.id ?? ''), name: text(contact.name), email: text(contact.email), jobTitle: text(contact.jobTitle), phone: text(contact.phone) })),
    contactsTotal: typeof dossier.contactsTotal === 'number' ? dossier.contactsTotal : 0,
    notesStatus: dossier.notesStatus === 'complete' || dossier.notesStatus === 'partial' || dossier.notesStatus === 'unavailable' ? dossier.notesStatus : 'unknown',
    notes: records(dossier.notes).flatMap((note) => {
      const body = text(note.body);
      return body ? [{ id: String(note.id ?? ''), body, timestamp: text(note.timestamp) }] : [];
    }),
    notesTotal: typeof dossier.notesTotal === 'number' ? dossier.notesTotal : 0,
  };
};

/** Queues one Kernel review of the account. No model call happens here. */
export const requestAccountReview = async (subjectId: string, requestId: string) => {
  const { data, error } = await supabase.functions.invoke('activity-feed', { body: { action: 'review_account', subjectId, requestId } });
  if (error) throw new Error((await toDashboardUiError(error, 'L’examen n’a pas pu être demandé. Réessayez dans un instant.')).message);
  if (!data || typeof data.topicId !== 'string') throw new Error('L’examen n’a pas pu être confirmé. Actualisez le compte.');
  return data as { topicId: string; threadId: string; jobId: string; reused?: boolean };
};

/** Reads the tenant's CRM again (companies, deals, contacts), e.g. after a
 * company was created from Domino. Explicit: never triggered by opening a page. */
export const syncCrmFromProvider = async (): Promise<void> => {
  const { error } = await supabase.functions.invoke('dashboard-sync-crm', { body: {} });
  if (error) throw new Error((await toDashboardUiError(error, 'HubSpot n’a pas pu être relu. Réessayez dans un instant.')).message);
};

// HubSpot default pipeline stage IDs. Custom stage IDs have no label in the
// cache yet: they are hidden rather than shown as internal identifiers.
const DEFAULT_STAGE_LABELS: Record<string, string> = {
  appointmentscheduled: 'Rendez-vous planifié',
  qualifiedtobuy: 'Qualifiée',
  presentationscheduled: 'Présentation planifiée',
  decisionmakerboughtin: 'Décideur convaincu',
  contractsent: 'Contrat envoyé',
  closedwon: 'Gagnée',
  closedlost: 'Perdue',
};
export const dealStageLabel = (stage: string | null): string | null => stage ? DEFAULT_STAGE_LABELS[stage] ?? null : null;

export const formatDealAmount = (amount: string | null): string | null => {
  const value = amount === null ? Number.NaN : Number(amount);
  return Number.isFinite(value) ? new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(value) : null;
};

export const formatCrmDate = (value: string | null): string | null => {
  const time = value === null ? Number.NaN : Date.parse(/^\d+$/.test(value) ? new Date(Number(value)).toISOString() : value);
  return Number.isFinite(time) ? new Date(time).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
};

// HubSpot default lifecycle stages; a custom stage has no label and is hidden.
const LIFECYCLE_LABELS: Record<string, string> = {
  subscriber: 'Abonné',
  lead: 'Lead',
  marketingqualifiedlead: 'Lead marketing',
  salesqualifiedlead: 'Lead commercial',
  opportunity: 'Opportunité',
  customer: 'Client',
  evangelist: 'Ambassadeur',
  other: 'Autre',
};
export const lifecycleStageLabel = (stage: string | null | undefined): string | null =>
  stage ? LIFECYCLE_LABELS[stage.toLowerCase()] ?? null : null;

/** « MARKETING_AND_ADVERTISING » → « Marketing and advertising »; HubSpot enums are English. */
export const industryLabel = (industry: string | null | undefined): string | null => {
  if (!industry) return null;
  if (!/^[A-Z0-9_]+$/.test(industry)) return industry;
  const words = industry.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** « 51 salariés », or the range HubSpot keeps (« 51-200 »). */
export const companySizeLabel = (employees: string | null | undefined, range: string | null | undefined): string | null => {
  const count = employees === null || employees === undefined ? Number.NaN : Number(employees);
  if (Number.isFinite(count) && count > 0) return `${new Intl.NumberFormat('fr-FR').format(count)} salariés`;
  return range ? `${range.replace(/\s+/g, '')} salariés` : null;
};
